import { useState } from "react";
import {
  Search,
  Layers,
  Sparkles,
  CheckCircle2,
  Calendar,
  MousePointerClick,
  Sliders,
  Maximize2,
  ChevronRight,
  Filter,
  Compass,
  LayoutGrid,
  FileImage,
} from "lucide-react";
import { Badge, Button } from "@/shared/ui";
import { TestUiCoreFormInputs } from "./core-showcase/TestUiCoreFormInputs";
import { TestUiCoreActionsFeedback } from "./core-showcase/TestUiCoreActionsFeedback";
import { TestUiCoreOverlays } from "./core-showcase/TestUiCoreOverlays";
import { TestUiCoreNavigationCards } from "./core-showcase/TestUiCoreNavigationCards";
import { TestUiCoreDataStates } from "./core-showcase/TestUiCoreDataStates";
import { TestUiCoreMediaShowcase } from "./core-showcase/TestUiCoreMediaShowcase";

type CoreCategory = "all" | "inputs" | "actions" | "overlays" | "navigation" | "states" | "media";

export function TestUiCoreSection() {
  const [activeCategory, setActiveCategory] = useState<CoreCategory>("all");
  const [searchQuery, setSearchQuery] = useState("");

  const categories: Array<{ id: CoreCategory; label: string; icon: typeof Layers; count: number }> = [
    { id: "all", label: "Tất cả Components", icon: Layers, count: 38 },
    { id: "inputs", label: "1. Form Inputs & Pickers", icon: Calendar, count: 8 },
    { id: "actions", label: "2. Buttons, Badges & Feedback", icon: MousePointerClick, count: 8 },
    { id: "overlays", label: "3. Overlays & Menus", icon: Sliders, count: 4 },
    { id: "navigation", label: "4. Navigation & Cards", icon: Compass, count: 6 },
    { id: "states", label: "5. Data States & FormField", icon: LayoutGrid, count: 7 },
    { id: "media", label: "6. Media, Crop & Upload", icon: FileImage, count: 5 },
  ];

  const q = searchQuery.toLowerCase().trim();

  // Match flags
  const matchInputs =
    activeCategory === "all" || activeCategory === "inputs"
      ? !q ||
        ["input", "datepicker", "date", "time", "datetime", "select", "searchable", "checkbox", "radio", "switch", "slider", "form", "chọn"].some((k) => k.includes(q) || q.includes(k))
      : false;

  const matchActions =
    activeCategory === "all" || activeCategory === "actions"
      ? !q ||
        ["button", "nút", "iconbutton", "badge", "huy hiệu", "status", "trạng thái", "progress", "tiến độ", "banner", "tooltip"].some((k) => k.includes(q) || q.includes(k))
      : false;

  const matchOverlays =
    activeCategory === "all" || activeCategory === "overlays"
      ? !q ||
        ["dropdown", "menu", "popover", "accordion", "collapsible", "dialog", "xác nhận", "confirm"].some((k) => k.includes(q) || q.includes(k))
      : false;

  const matchNavigation =
    activeCategory === "all" || activeCategory === "navigation"
      ? !q ||
        ["kpi", "kpicard", "card", "thẻ", "tab", "tabs", "breadcrumb", "đường dẫn", "pagination", "phân trang", "trang", "timeline", "tiến trình", "progress"].some((k) => k.includes(q) || q.includes(k))
      : false;

  const matchStates =
    activeCategory === "all" || activeCategory === "states"
      ? !q ||
        ["formfield", "form", "empty", "rỗng", "error", "lỗi", "skeleton", "shimmer", "loading", "drawer", "modal", "toast", "thông báo"].some((k) => k.includes(q) || q.includes(k))
      : false;

  const matchMedia =
    activeCategory === "all" || activeCategory === "media"
      ? !q ||
        ["media", "image", "ảnh", "hình", "avatar", "crop", "cắt", "upload", "tải", "file", "tệp", "dropzone", "lightbox", "xem"].some((k) => k.includes(q) || q.includes(k))
      : false;

  return (
    <div className="space-y-6 animate-in fade-in duration-200">
      {/* Overview Intro Banner */}
      <div className="rounded-control border border-border bg-surface p-5 shadow-xs">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-start gap-3.5">
            <div className="flex size-11 shrink-0 items-center justify-center rounded-control bg-primary text-on-primary shadow-sm">
              <Layers className="size-6" />
            </div>
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <h2 className="m-0 text-h2 font-bold tracking-tight text-text">
                  Bộ Sưu Tập Core UI Components (Số 7)
                </h2>
                <Badge tone="success" label="32+ Components Chuẩn ERP" />
                <Badge tone="primary" label="100% Radix & Tailwind" />
              </div>
              <p className="mt-1 mb-0 text-body text-text-muted max-w-3xl">
                Toàn bộ các nguyên mẫu UI cốt lõi được sử dụng thường xuyên nhất trong ERP TM Food: DatePicker tiếng Việt kèm giờ/phút, SearchableSelect có tìm kiếm & đóng chéo, KpiCard, Radix Tabs, Breadcrumb, Pagination, Empty/Error States, Skeleton, FormField, Drawer, Modal và Toasts.
              </p>
            </div>
          </div>

          {/* Quick Specifications Strip */}
          <div className="flex flex-wrap md:flex-col gap-2 shrink-0 text-caption border-t md:border-t-0 md:border-l border-border/80 pt-3 md:pt-0 md:pl-4">
            <div className="flex items-center gap-2 text-text font-medium">
              <CheckCircle2 className="size-4 text-success" />
              <span>Chuẩn input: <strong className="font-mono text-primary">h-[36px] min-h-[36px]</strong></span>
            </div>
            <div className="flex items-center gap-2 text-text font-medium">
              <CheckCircle2 className="size-4 text-success" />
              <span>Đóng chéo: <strong className="text-primary">Mutual-Exclusion</strong></span>
            </div>
            <div className="flex items-center gap-2 text-text font-medium">
              <CheckCircle2 className="size-4 text-success" />
              <span>Accessibility: <strong className="text-primary">WCAG 2.1 AA</strong></span>
            </div>
          </div>
        </div>

        {/* Filter Toolbar */}
        <div className="mt-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-t border-border/60 pt-4">
          {/* Category Chips */}
          <div className="flex flex-wrap items-center gap-1.5">
            {categories.map((cat) => {
              const Icon = cat.icon;
              const isActive = activeCategory === cat.id;
              return (
                <button
                  key={cat.id}
                  type="button"
                  onClick={() => setActiveCategory(cat.id)}
                  className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-caption font-semibold transition-all cursor-pointer ${
                    isActive
                      ? "bg-primary text-on-primary shadow-xs"
                      : "bg-bg text-text-secondary hover:bg-border/60 hover:text-text"
                  }`}
                >
                  <Icon className="size-3.5" />
                  <span>{cat.label}</span>
                </button>
              );
            })}
          </div>

          {/* Search Input */}
          <div className="relative w-full sm:w-64">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 size-3.5 text-text-muted pointer-events-none" />
            <input
              type="text"
              placeholder="Tìm component (kpicard, tabs, empty...)"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full rounded-control border border-border bg-bg pl-6 pr-3 py-1.5 text-caption text-text placeholder:text-text-muted focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-caption text-text-muted hover:text-text cursor-pointer"
              >
                ×
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Sections rendering based on filter/search */}
      {matchInputs && (
        <section className="space-y-3">
          <TestUiCoreFormInputs />
        </section>
      )}

      {matchActions && (
        <section className="space-y-3">
          <TestUiCoreActionsFeedback />
        </section>
      )}

      {matchOverlays && (
        <section className="space-y-3">
          <TestUiCoreOverlays />
        </section>
      )}

      {matchNavigation && (
        <section className="space-y-3">
          <TestUiCoreNavigationCards />
        </section>
      )}

      {matchStates && (
        <section className="space-y-3">
          <TestUiCoreDataStates />
        </section>
      )}

      {matchMedia && (
        <section className="space-y-3">
          <TestUiCoreMediaShowcase />
        </section>
      )}

      {!matchInputs && !matchActions && !matchOverlays && !matchNavigation && !matchStates && !matchMedia && (
        <div className="rounded-control border border-border bg-surface p-12 text-center space-y-2">
          <Layers className="size-8 text-text-muted mx-auto" />
          <p className="text-body font-bold text-text">Không tìm thấy component nào phù hợp</p>
          <p className="text-caption text-text-muted">
            Không có component nào khớp với từ khóa "{searchQuery}". Vui lòng thử lại với từ khóa khác như "kpicard", "tabs", "pagination", "empty", "skeleton".
          </p>
          <Button size="sm" variant="secondary" onClick={() => setSearchQuery("")}>
            Xóa Tìm Kiếm
          </Button>
        </div>
      )}
    </div>
  );
}
